'use client'

import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { BookOpen, Check, Sparkles, Zap, Crown, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { InstrumentedButton } from '@/components/ui/instrumented-button'
import { createClient } from '@/lib/supabase/client'
import { AppErrorBoundary } from '@/components/common/AppErrorBoundary'
import { useFontFamily, useFontSize } from '@/contexts/preferences-context'

interface Profile {
  subscription_tier: string
  subscription_status: string
  credits_balance: number
  email: string
}

export default function PricingPage() {
  const router = useRouter()
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [subscribing, setSubscribing] = useState<string | null>(null)
  const { fontFamily } = useFontFamily()
  const { fontSize } = useFontSize()

  useEffect(() => {
    document.title = 'Pricing - Infinite Pages'
    loadProfile()
  }, [])

  async function loadProfile() {
    const supabase = createClient()
    const { data: { user } } = await supabase.auth.getUser()

    if (user) {
      const { data } = await supabase
        .from('profiles')
        .select('subscription_tier, subscription_status, credits_balance, email')
        .eq('id', user.id)
        .single()

      setProfile(data)
    }
    setLoading(false)
  }

  async function handleSubscribe(tier: 'reader' | 'creator' | 'credit_refill') {
    setSubscribing(tier)

    try {
      const supabase = createClient()
      const { data: { user } } = await supabase.auth.getUser()

      if (!user) {
        router.push('/auth/signin?redirect=/billing')
        return
      }

      // Call checkout API
      const response = await fetch('/api/billing/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier })
      })

      const data = await response.json()

      if (data.url) {
        window.location.href = data.url
      } else {
        alert(data.error || 'Failed to create checkout session')
        setSubscribing(null)
      }
    } catch (error) {
      console.error('Subscription error:', error)
      alert('Failed to start subscription process')
      setSubscribing(null)
    }
  }

  const currentTier = profile?.subscription_tier || 'free'
  const isActive = profile?.subscription_status === 'active'

  return (
    <AppErrorBoundary source="billing-page">
      {/* Monitoring boundary ensures ErrorBoundary coverage from '@/components/common/ErrorMonitoringV2' */}
      <div className="relative min-h-screen overflow-hidden">
      {/* Background Image with Overlay */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url("/victorian-street-scene.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed'
        }}
      />

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/40 z-10" />

      {/* Content */}
      <div className={`relative z-20 min-h-screen font-${fontFamily} font-${fontSize}`}>
        {/* Navigation */}
        <nav className="px-6 py-4 flex justify-between items-center border-b border-theme-border-primary backdrop-blur-glass">
          <Link href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <ArrowLeft className="w-5 h-5 accent-text-primary" />
            <BookOpen className="w-8 h-8 accent-text-primary" />
            <h1 className="text-2xl font-bold text-theme-text-primary">Infinite Pages</h1>
          </Link>
          <div className="flex gap-4">
            {profile ? (
              <>
                <Link href="/dashboard">
                  <Button variant="ghost" className="text-theme-text-primary hover:accent-text-primary hover:bg-theme-bg-secondary">
                    Dashboard
                  </Button>
                </Link>
                {currentTier !== 'free' && (
                  <span className="px-3 py-2 rounded-lg bg-theme-bg-elevated border border-accent-primary/40 accent-text-primary text-sm font-medium">
                    {currentTier.charAt(0).toUpperCase() + currentTier.slice(1)} Plan
                  </span>
                )}
              </>
            ) : (
              <>
                <Link href="/auth/signin">
                  <Button variant="ghost" className="text-theme-text-primary hover:accent-text-primary hover:bg-theme-bg-secondary">
                    Sign In
                  </Button>
                </Link>
                <Link href="/auth/signup">
                  <Button className="accent-bg-primary accent-hover-primary text-[var(--color-text-on-accent)]">
                    Sign Up
                  </Button>
                </Link>
              </>
            )}
          </div>
        </nav>

        {/* Hero Section */}
        <div className="px-6 py-16 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-theme-text-primary mb-4">
            Choose Your <span className="text-theme-text-primary">Writing Journey</span>
          </h1>
          <p className="text-xl text-theme-text-secondary max-w-2xl mx-auto">
            From casual reader to professional creator. Pick the plan that fits your story.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="px-6 pb-20">
          <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

            {/* Free Tier */}
            <div className="bg-theme-bg-tertiary backdrop-blur-glass rounded-xl p-8 border border-theme-border-card hover:border-theme-border-secondary transition-all shadow-theme-md hover:shadow-theme-lg">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-6 h-6 text-theme-text-primary" />
                <h3 className="text-2xl font-bold text-theme-text-primary">FREE</h3>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-bold text-theme-text-primary">$0</span>
                <span className="text-theme-text-primary ml-2 opacity-70">/month</span>
              </div>
              <p className="text-theme-text-secondary mb-6 text-sm">
                Sample our library
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2 text-theme-text-secondary text-sm">
                  <Check className="w-5 h-5 accent-text-primary shrink-0 mt-0.5" />
                  <span>One complete story per genre</span>
                </li>
                <li className="flex items-start gap-2 text-theme-text-secondary text-sm">
                  <Check className="w-5 h-5 accent-text-primary shrink-0 mt-0.5" />
                  <span>Browse what&apos;s available</span>
                </li>
                <li className="flex items-start gap-2 text-theme-text-primary text-sm opacity-70">
                  <span className="w-5 h-5 shrink-0 mt-0.5 text-center">✕</span>
                  <span>No story creation</span>
                </li>
                <li className="flex items-start gap-2 text-theme-text-primary text-sm opacity-70">
                  <span className="w-5 h-5 shrink-0 mt-0.5 text-center">✕</span>
                  <span>No AI generation</span>
                </li>
              </ul>
              {currentTier === 'free' ? (
                <Button disabled className="w-full bg-theme-bg-secondary text-theme-text-primary cursor-not-allowed opacity-50">
                  Current Plan
                </Button>
              ) : (
                <Link href="/my-library">
                  <Button variant="outline" className="w-full border-theme-border-primary text-theme-text-primary hover:bg-theme-bg-secondary">
                    Browse Library
                  </Button>
                </Link>
              )}
            </div>

            {/* Reader Tier */}
            <div className="bg-theme-bg-tertiary backdrop-blur-glass rounded-xl p-8 border border-theme-border-info hover:border-theme-border-info transition-all shadow-theme-md hover:shadow-theme-lg">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-6 h-6 text-theme-text-info" />
                <h3 className="text-2xl font-bold text-theme-text-primary">READER</h3>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-bold text-theme-text-primary">$4.99</span>
                <span className="text-theme-text-primary ml-2 opacity-70">/month</span>
              </div>
              <p className="text-theme-text-secondary mb-6 text-sm">
                Unlimited reading access
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2 text-theme-text-secondary text-sm">
                  <Check className="w-5 h-5 accent-text-primary shrink-0 mt-0.5" />
                  <span>All published stories</span>
                </li>
                <li className="flex items-start gap-2 text-theme-text-secondary text-sm">
                  <Check className="w-5 h-5 accent-text-primary shrink-0 mt-0.5" />
                  <span>Export stories as PDF</span>
                </li>
                <li className="flex items-start gap-2 text-theme-text-secondary text-sm">
                  <Check className="w-5 h-5 accent-text-primary shrink-0 mt-0.5" />
                  <span>Support authors</span>
                </li>
                <li className="flex items-start gap-2 text-theme-text-primary text-sm opacity-70">
                  <span className="w-5 h-5 shrink-0 mt-0.5 text-center">✕</span>
                  <span>No story creation</span>
                </li>
              </ul>
              {currentTier === 'reader' && isActive ? (
                <Button disabled className="w-full bg-theme-bg-secondary text-theme-text-primary cursor-not-allowed opacity-50">
                  Current Plan
                </Button>
              ) : (
                <InstrumentedButton
                  eventName="billing.subscription.update"
                  component="billing-page"
                  errorMessage="Payment action failed"
                  onClick={() => handleSubscribe('reader')}
                  disabled={subscribing !== null}
                  className="w-full accent-bg-primary accent-hover-primary text-[var(--color-text-on-accent)]"
                >
                  {subscribing === 'reader' ? 'Loading...' : 'Subscribe'}
                </InstrumentedButton>
              )}
            </div>

            {/* Creator Tier - POPULAR */}
            <div className="bg-theme-bg-tertiary backdrop-blur-glass rounded-xl p-8 border-2 accent-border-primary/50 hover:accent-border-primary transition-all relative shadow-theme-lg">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 accent-bg-primary text-[var(--color-text-on-accent)] px-4 py-1 rounded-full text-xs font-bold">
                POPULAR
              </div>
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-6 h-6 accent-text-primary" />
                <h3 className="text-2xl font-bold text-theme-text-primary">CREATOR</h3>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-bold text-theme-text-primary">$9.99</span>
                <span className="text-theme-text-primary ml-2 opacity-70">/month</span>
              </div>
              <p className="text-theme-text-secondary mb-6 text-sm">
                Everything in Reader
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2 text-theme-text-secondary text-sm">
                  <Check className="w-5 h-5 accent-text-primary shrink-0 mt-0.5" />
                  <span>~2000 AI credits/month</span>
                </li>
                <li className="flex items-start gap-2 text-theme-text-secondary text-sm">
                  <Check className="w-5 h-5 accent-text-primary shrink-0 mt-0.5" />
                  <span>Generate story bible</span>
                </li>
                <li className="flex items-start gap-2 text-theme-text-secondary text-sm">
                  <Check className="w-5 h-5 accent-text-primary shrink-0 mt-0.5" />
                  <span>Generate chapters</span>
                </li>
                <li className="flex items-start gap-2 text-theme-text-secondary text-sm">
                  <Check className="w-5 h-5 accent-text-primary shrink-0 mt-0.5" />
                  <span>Unlimited stories & publishing</span>
                </li>
                <li className="flex items-start gap-2 text-theme-text-secondary text-sm">
                  <Check className="w-5 h-5 accent-text-primary shrink-0 mt-0.5" />
                  <span>Choose Haiku or Sonnet models</span>
                </li>
                <li className="flex items-start gap-2 text-theme-text-secondary text-sm">
                  <Check className="w-5 h-5 accent-text-primary shrink-0 mt-0.5" />
                  <span>Export in all formats</span>
                </li>
              </ul>
              {currentTier === 'creator' && isActive ? (
                <Button disabled className="w-full bg-theme-bg-secondary text-theme-text-primary cursor-not-allowed opacity-50">
                  Current Plan
                </Button>
              ) : (
                <InstrumentedButton
                  eventName="billing.subscription.update"
                  component="billing-page"
                  errorMessage="Payment action failed"
                  onClick={() => handleSubscribe('creator')}
                  disabled={subscribing !== null}
                  className="w-full accent-bg-primary accent-hover-primary text-[var(--color-text-on-accent)] shadow-lg shadow-theme-lg"
                >
                  {subscribing === 'creator' ? 'Loading...' : 'Start Creating'}
                </InstrumentedButton>
              )}
            </div>

            {/* Credit Refill Option */}
            <div className="bg-theme-bg-tertiary backdrop-blur-glass rounded-xl p-8 border accent-border-primary/30 hover:accent-border-primary/60 transition-all shadow-theme-md hover:shadow-theme-lg">
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-6 h-6 accent-text-primary" />
                <h3 className="text-2xl font-bold text-theme-text-primary">CREDIT REFILL</h3>
              </div>
              <div className="mb-6">
                <span className="text-4xl font-bold text-theme-text-primary">$9.99</span>
                <span className="text-theme-text-primary ml-2 opacity-70">one-time</span>
              </div>
              <p className="text-theme-text-secondary mb-6 text-sm">
                For existing creators only
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2 text-theme-text-secondary text-sm">
                  <Check className="w-5 h-5 accent-text-primary shrink-0 mt-0.5" />
                  <span>~2000 additional credits</span>
                </li>
                <li className="flex items-start gap-2 text-theme-text-secondary text-sm">
                  <Check className="w-5 h-5 accent-text-primary shrink-0 mt-0.5" />
                  <span>No reading fee included</span>
                </li>
                <li className="flex items-start gap-2 text-theme-text-secondary text-sm">
                  <Check className="w-5 h-5 accent-text-primary shrink-0 mt-0.5" />
                  <span>Use immediately</span>
                </li>
                <li className="flex items-start gap-2 text-theme-text-secondary text-sm">
                  <Check className="w-5 h-5 accent-text-primary shrink-0 mt-0.5" />
                  <span>Stack with monthly credits</span>
                </li>
              </ul>
              {currentTier === 'free' || currentTier === 'reader' ? (
                <Button disabled className="w-full bg-theme-bg-secondary text-theme-text-primary cursor-not-allowed opacity-50">
                  Creator Plan Required
                </Button>
              ) : (
                <InstrumentedButton
                  eventName="billing.credits.purchase"
                  component="billing-page"
                  errorMessage="Payment action failed"
                  onClick={() => handleSubscribe('credit_refill')}
                  disabled={subscribing !== null}
                  className="w-full accent-bg-primary accent-hover-primary text-[var(--color-text-on-accent)]"
                >
                  {subscribing === 'credit_refill' ? 'Loading...' : 'Buy Credits'}
                </InstrumentedButton>
              )}
            </div>

          </div>
        </div>

        {/* FAQ Section */}
        <div className="px-6 pb-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-theme-text-primary mb-12">
              Frequently Asked <span className="text-theme-text-primary">Questions</span>
            </h2>

            <div className="space-y-6">
              <div className="bg-theme-bg-tertiary backdrop-blur-glass rounded-lg p-6 border border-theme-border-card shadow-theme-sm">
                <h3 className="text-theme-text-primary font-semibold text-lg mb-2">What&apos;s included in the Free tier?</h3>
                <p className="text-theme-text-secondary">
                  The Free tier gives you one complete story per genre to sample our platform. You can browse what&apos;s available and see the quality of our AI-generated content without any cost. No story creation or AI generation is included.
                </p>
              </div>

              <div className="bg-theme-bg-tertiary backdrop-blur-glass rounded-lg p-6 border border-theme-border-card shadow-theme-sm">
                <h3 className="text-theme-text-primary font-semibold text-lg mb-2">What&apos;s the difference between Reader and Creator?</h3>
                <p className="text-theme-text-secondary">
                  Reader ($4.99/month) gives you unlimited reading access to all published stories, PDF export, and the ability to support authors. Creator ($9.99/month) includes everything in Reader plus ~2000 AI credits per month for story creation, bible generation, and unlimited publishing.
                </p>
              </div>

              <div className="bg-theme-bg-tertiary backdrop-blur-glass rounded-lg p-6 border border-theme-border-card shadow-theme-sm">
                <h3 className="text-theme-text-primary font-semibold text-lg mb-2">Can I buy additional credits?</h3>
                <p className="text-theme-text-secondary">
                  Yes! Creator plan users can purchase Credit Refills ($9.99 for ~2000 credits) as one-time purchases. These stack with your monthly credits and can be used immediately. Perfect for power users who need extra credits for intensive writing sessions.
                </p>
              </div>

              <div className="bg-theme-bg-tertiary backdrop-blur-glass rounded-lg p-6 border border-theme-border-card shadow-theme-sm">
                <h3 className="text-theme-text-primary font-semibold text-lg mb-2">How do model choices affect credits?</h3>
                <p className="text-theme-text-secondary">
                  <strong>Haiku:</strong> Fast & efficient (~4-6 credits/chapter)<br/>
                  <strong>Sonnet:</strong> High quality (~15-20 credits/chapter)<br/>
                  Creator plan users can choose between Haiku and Sonnet models. You choose the model that fits your needs!
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="px-6 pb-20">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-accent-primary/40 to-accent-primary/40 backdrop-blur-md rounded-2xl p-12 border-accent-primary/40 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-theme-text-primary mb-4">
              Ready to Start Writing?
            </h2>
            <p className="text-theme-text-secondary text-lg mb-8">
              Join writers creating unlimited AI-powered stories
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => handleSubscribe('creator')}
                size="lg"
                className="accent-bg-primary accent-hover-primary text-[var(--color-text-on-accent)] text-lg px-8 py-6"
              >
                Start with Creator
              </Button>
              <Link href="/auth/signup">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 accent-border-primary/50 accent-text-primary hover:bg-theme-bg-elevated hover:border-accent-primary/50 text-lg px-8 py-6"
                >
                  Try Free First
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="py-6 text-center text-theme-text-muted text-sm border-t border-theme-border-primary">
          <p>&copy; 2025 Infinite Pages. Powered by Claude AI.</p>
        </footer>
      </div>
      </div>
    </AppErrorBoundary>
  )
}
